import React, { useState } from 'react';
import { NavLink, Link, Routes, Route, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, ScrollText, ShieldAlert, 
  CircleDollarSign, BarChart3, Settings, LogOut, Menu, X, 
  Cpu, Activity, History, UserCheck, Bell
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import UserProjectInspector from './pages/UserProjectInspector';
import GenerationLogs from './pages/GenerationLogs';
import ContentModeration from './pages/ContentModeration';
import EconomySettings from './pages/EconomySettings';
import Analytics from './pages/Analytics';
import PlatformSettings from './pages/PlatformSettings';
import NotificationCenter from './pages/NotificationCenter';

// Pollinations Pages
import PollinationsProfile from './pages/pollinations/PollinationsProfile';
import PollinationsBalance from './pages/pollinations/PollinationsBalance';
import PollinationsUsageHistory from './pages/pollinations/PollinationsUsageHistory';
import PollinationsDailyUsage from './pages/pollinations/PollinationsDailyUsage';

import logo from "@/assets/logo.png";

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const menuItems = [
    { section: 'System', items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
      { label: 'Users', icon: Users, path: '/admin/users' },
      { label: 'Platform Logs', icon: ScrollText, path: '/admin/logs' },
      { label: 'Broadcast Center', icon: Bell, path: '/admin/notifications' },
    ]},
    { section: 'Pollinations.ai', items: [
      { label: 'Provider Profile', icon: UserCheck, path: '/admin/pollinations/profile' },
      { label: 'Pollen Balance', icon: CircleDollarSign, path: '/admin/pollinations/balance' },
      { label: 'Usage History', icon: History, path: '/admin/pollinations/history' },
      { label: 'Daily Analytics', icon: Activity, path: '/admin/pollinations/analytics' },
    ]},
    { section: 'Platform', items: [
      { label: 'Moderation', icon: ShieldAlert, path: '/admin/moderation' },
      { label: 'Economy', icon: CircleDollarSign, path: '/admin/economy' },
      { label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
      { label: 'Settings', icon: Settings, path: '/admin/settings' },
    ]}
  ];

  return (
    <div className="flex h-screen bg-sonas-bg text-sonas-text font-sans">
      {/* Mobile Sidebar Toggle */}
      <button 
        className="lg:hidden fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-sonas-accent text-sonas-bg shadow-xl flex items-center justify-center"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 bg-sonas-surface border-r border-sonas-border transition-all duration-300 lg:relative",
        isSidebarOpen ? "w-64" : "w-0 -translate-x-full lg:w-20 lg:translate-x-0"
      )}>
        <div className="flex flex-col h-full p-4 overflow-y-auto sonas-scrollbar">
          <div className="flex items-center mb-8 px-2 overflow-hidden">
            <img src={logo} alt="Sonas" className="h-6 w-auto shrink-0" />
          </div>

          <nav className="flex-1 space-y-8">
            {menuItems.map((group) => (
              <div key={group.section} className="space-y-2">
                {isSidebarOpen && (
                  <h3 className="px-3 text-[10px] font-bold uppercase tracking-widest text-sonas-dim">
                    {group.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/admin'}
                      className={({ isActive }) => cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative",
                        isActive 
                          ? "bg-sonas-accent/10 text-sonas-accent" 
                          : "text-sonas-muted hover:bg-sonas-surface2 hover:text-sonas-text"
                      )}
                    >
                      <item.icon size={20} className="shrink-0" />
                      <span className={cn("text-sm font-medium whitespace-nowrap", !isSidebarOpen && "lg:hidden")}>
                        {item.label}
                      </span>
                      {!isSidebarOpen && (
                        <div className="absolute left-16 px-2 py-1 bg-sonas-surface2 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          {item.label}
                        </div>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="mt-auto pt-4 border-t border-sonas-border">
            <Link to="/app" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sonas-muted hover:bg-sonas-surface2 hover:text-sonas-text transition-all">
              <LogOut size={20} />
              <span className={cn("text-sm font-medium", !isSidebarOpen && "lg:hidden")}>Exit Admin</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 border-b border-sonas-border flex items-center justify-between px-6 bg-sonas-surface/50 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <button 
              className="hidden lg:flex h-8 w-8 items-center justify-center rounded-lg hover:bg-sonas-surface2 text-sonas-dim"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <Menu size={18} />
            </button>
            <h2 className="text-sm font-syne font-bold uppercase tracking-widest text-sonas-dim">
              System Console
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1 bg-sonas-accent/10 rounded-full border border-sonas-accent/20">
              <div className="h-1.5 w-1.5 rounded-full bg-sonas-accent animate-pulse" />
              <span className="text-[10px] font-bold text-sonas-accent uppercase tracking-tighter">Live Monitor</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 lg:p-10 sonas-scrollbar">
          <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/users/:userId/projects" element={<UserProjectInspector />} />
            <Route path="/logs" element={<GenerationLogs />} />
            <Route path="/moderation" element={<ContentModeration />} />
            <Route path="/economy" element={<EconomySettings />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/settings" element={<PlatformSettings />} />
            <Route path="/notifications" element={<NotificationCenter />} />
            
            {/* Pollinations Routes */}
            <Route path="/pollinations/profile" element={<PollinationsProfile />} />
            <Route path="/pollinations/balance" element={<PollinationsBalance />} />
            <Route path="/pollinations/history" element={<PollinationsUsageHistory />} />
            <Route path="/pollinations/analytics" element={<PollinationsDailyUsage />} />

            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;